


import React, { useState, useEffect, useMemo } from 'react';
import { DashboardHeader } from './DashboardHeader';
import { DataContractCard } from './DataContractCard';
import { DataContractDetailsModal } from './DataContractDetailsModal';
import axios from 'axios';
import heartLoading from "../../../assests/heartLoading.gif";
import { RotateCcw, Filter } from "lucide-react";
import InfoIcon from '@mui/icons-material/Info';
import { IconButton, Tooltip } from '@mui/material';
import { replace, useLocation, useNavigate } from 'react-router-dom';
import Select from "react-select";



function LandingPage() {
   // Data states
  const [dataContracts, setDataContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  
  // Filter states - separate temporary selections from applied filters
  const [parentDomain, setParentDomain] = useState("All Parent Domains");
  const [domain, setDomain] = useState("All Domains");
  const [subDomain, setSubDomain] = useState("All Sub Domains");
  const [product, setProduct] = useState("All Products");
  const [showFilters, setShowFilters] = useState(false);
  
  // Applied filters (used for API calls)
  const [appliedParentDomain, setAppliedParentDomain] = useState("All Parent Domains");
  const [appliedDomain, setAppliedDomain] = useState("All Domains");
  const [appliedSubDomain, setAppliedSubDomain] = useState("All Sub Domains");
  const [appliedProduct, setAppliedProduct] = useState("All Products");
  
  // Hierarchical filters from API
  const [hierarchicalFilters, setHierarchicalFilters] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    parentDomains: [],
    domains: [],
    subDomains: [],
    products: [],
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Modal states
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false)
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const viewProduct = params.get("viewProduct");
  // Fetch traffic light data from API
  const fetchTrafficLightData = async (payload = null) => {
    try {
      const requestPayload = payload || {
        parentDomain: appliedParentDomain === "All Parent Domains" ? "" : appliedParentDomain,
        domain: appliedDomain === "All Domains" ? "" : appliedDomain,
        subDomain: appliedSubDomain === "All Sub Domains" ? "" : appliedSubDomain,
        product: viewProduct ? viewProduct : appliedProduct === "All Products" ? "" : appliedProduct,
        searchValue: "",
        page: currentPage,
        pageSize: pageSize,
      };

      const apiEndpoint = `${process.env.REACT_APP_API_URL}/get-trafficlight-results`;

      const response = await axios.get(apiEndpoint, {params: requestPayload});

      if (response.data) {
        setDataContracts(response.data.dataContracts || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(response.data.totalPages || 0);
        setCurrentPage(response.data.page || 1);
        setPageSize(response.data.pageSize || 10);

        // Handle filter options from API response
        if (response.data.filters && response.data.filters.parent_domains) {
          setHierarchicalFilters(response.data.filters);

          // Extract ALL available options for each filter level
          const parentDomains = response.data.filters.parent_domains.map(
            (pd) => pd.parent_domain
          );

          // Get ALL domains
          const allDomains = [];
          response.data.filters.parent_domains.forEach((pd) => {
            pd.domains.forEach((d) => {
              if (!allDomains.includes(d.domain)) {
                allDomains.push(d.domain);
              }
            });
          });

          // Get ALL sub domains
          const allSubDomains = [];
          response.data.filters.parent_domains.forEach((pd) => {
            pd.domains.forEach((d) => {
              d.sub_domains.forEach((sd) => {
                if (!allSubDomains.includes(sd.sub_domain)) {
                  allSubDomains.push(sd.sub_domain);
                }
              });
            });
          });

          // Get ALL products
          const allProducts = [];
          response.data.filters.parent_domains.forEach((pd) => {
            pd.domains.forEach((d) => {
              d.sub_domains.forEach((sd) => {
                sd.products.forEach((p) => {
                  if (!allProducts.includes(p)) {
                    allProducts.push(p);
                  }
                });
              });
            });
          });

          setFilterOptions({
            parentDomains: parentDomains,
            domains: allDomains,
            subDomains: allSubDomains,
            products: allProducts,
          });
        }

        if (viewProduct && response.data?.dataContracts?.length) {
          handleViewDetails(response.data.dataContracts[0])
        }
      }
    } catch (error) {
      console.error("Error fetching traffic light data:", error);
      setDataContracts([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    setLoading(true);
    fetchTrafficLightData();
  }, [viewProduct]);

  // Show all contracts without status filtering (status filter is commented out)
  const filteredContracts = useMemo(() => {
    return dataContracts;
  }, [dataContracts]);

  const stats = useMemo(() => ({
    total: dataContracts.length,
    healthy: dataContracts.filter(c => c.status === 'healthy').length,
    warning: dataContracts.filter(c => c.status === 'warning').length,
    critical: dataContracts.filter(c => c.status === 'critical').length
  }), [dataContracts]);

  // Filter change handlers with cascading logic (no API calls)
  const handleParentDomainChange = (event) => {
    const newParentDomain = event.target.value;
    setParentDomain(newParentDomain);

    if (newParentDomain === "All Parent Domains") {
      setDomain("All Domains");
      setSubDomain("All Sub Domains");
      setProduct("All Products");
    } else if (hierarchicalFilters) {
      const selectedParentDomain = hierarchicalFilters.parent_domains.find(
        (pd) => pd.parent_domain === newParentDomain
      );

      if (selectedParentDomain) {
        const relatedDomains = selectedParentDomain.domains.map((d) => d.domain);

        if (relatedDomains.length === 1) {
          setDomain(relatedDomains[0]);
          const selectedDomain = selectedParentDomain.domains[0];
          const domainSubDomains = selectedDomain.sub_domains.map((sd) => sd.sub_domain);

          if (domainSubDomains.length === 1) {
            setSubDomain(domainSubDomains[0]);
            const selectedSubDomain = selectedDomain.sub_domains[0];
            const subDomainProducts = selectedSubDomain.products;

            if (subDomainProducts.length === 1) {
              setProduct(subDomainProducts[0]);
            } else {
              setProduct("All Products");
            }
          } else {
            setSubDomain("All Sub Domains");
            setProduct("All Products");
          }
        } else {
          setDomain("All Domains");
          setSubDomain("All Sub Domains");
          setProduct("All Products");
        }
      }
    }
  };

  const handleDomainChange = (event) => {
    const newDomain = event.target.value;
    setDomain(newDomain);

    if (newDomain === "All Domains") {
      setSubDomain("All Sub Domains");
      setProduct("All Products");
    } else if (hierarchicalFilters) {
      const selectedParentDomain = hierarchicalFilters.parent_domains.find(
        (pd) => pd.parent_domain === parentDomain
      );

      if (selectedParentDomain) {
        const selectedDomain = selectedParentDomain.domains.find(
          (d) => d.domain === newDomain
        );

        if (selectedDomain) {
          const relatedSubDomains = selectedDomain.sub_domains.map((sd) => sd.sub_domain);

          if (relatedSubDomains.length === 1) {
            setSubDomain(relatedSubDomains[0]);
            const selectedSubDomain = selectedDomain.sub_domains[0];
            const subDomainProducts = selectedSubDomain.products;

            if (subDomainProducts.length === 1) {
              setProduct(subDomainProducts[0]);
            } else {
              setProduct("All Products");
            }
          } else {
            setSubDomain("All Sub Domains");
            setProduct("All Products");
          }
        }
      }
    }
  };

  const handleSubDomainChange = (event) => {
    const newSubDomain = event.target.value;
    setSubDomain(newSubDomain);

    if (newSubDomain === "All Sub Domains") {
      setProduct("All Products");
    } else if (hierarchicalFilters) {
      const selectedParentDomain = hierarchicalFilters.parent_domains.find(
        (pd) => pd.parent_domain === parentDomain
      );

      if (selectedParentDomain) {
        const selectedDomain = selectedParentDomain.domains.find(
          (d) => d.domain === domain
        );

        if (selectedDomain) {
          const selectedSubDomain = selectedDomain.sub_domains.find(
            (sd) => sd.sub_domain === newSubDomain
          );

          if (selectedSubDomain) {
            const relatedProducts = selectedSubDomain.products;

            if (relatedProducts.length === 1) {
              setProduct(relatedProducts[0]);
            } else {
              setProduct("All Products");
            }
          }
        }
      }
    }
  };

  const handleProductChange = (event) => {
    const newProduct = event.target.value;
    setProduct(newProduct);
  };

  // Apply filters handler - triggers API call
  const handleApplyFilters = () => {
    setAppliedParentDomain(parentDomain);
    setAppliedDomain(domain);
    setAppliedSubDomain(subDomain);
    setAppliedProduct(product);
    setCurrentPage(1);
    setFilterLoading(true);

    const payload = {
      parentDomain: parentDomain === "All Parent Domains" ? "" : parentDomain,
      domain: domain === "All Domains" ? "" : domain,
      subDomain: subDomain === "All Sub Domains" ? "" : subDomain,
      product: product === "All Products" ? "" : product,
      searchValue: "",
      page: 1,
      pageSize: pageSize,
    };

    fetchTrafficLightData(payload);
  };

  // Get filtered options based on current selections
  const getFilterOptions = () => {
    let availableOptions = { ...filterOptions };

    if (hierarchicalFilters && hierarchicalFilters.parent_domains) {
      if (parentDomain !== "All Parent Domains") {
        const selectedParentDomain = hierarchicalFilters.parent_domains.find(
          (pd) => pd.parent_domain === parentDomain
        );

        if (selectedParentDomain) {
          const domains = selectedParentDomain.domains.map((d) => d.domain);
          availableOptions.domains = domains;

          if (domain !== "All Domains") {
            const selectedDomain = selectedParentDomain.domains.find(
              (d) => d.domain === domain
            );
            if (selectedDomain) {
              const subDomains = selectedDomain.sub_domains.map((sd) => sd.sub_domain);
              availableOptions.subDomains = subDomains;

              if (subDomain !== "All Sub Domains") {
                const selectedSubDomain = selectedDomain.sub_domains.find(
                  (sd) => sd.sub_domain === subDomain
                );
                if (selectedSubDomain) {
                  availableOptions.products = selectedSubDomain.products;
                }
              }
            }
          }
        }
      }
    }

    return availableOptions;
  };

  const handleResetFilters = () => {
    setParentDomain("All Parent Domains");
    setDomain("All Domains");
    setSubDomain("All Sub Domains");
    setProduct("All Products");
    setAppliedParentDomain("All Parent Domains");
    setAppliedDomain("All Domains");
    setAppliedSubDomain("All Sub Domains");
    setAppliedProduct("All Products");
    setCurrentPage(1);
    setFilterLoading(true);
    
    // Pass empty payload explicitly to reset filters
    const payload = {
      parentDomain: "",
      domain: "",
      subDomain: "",
      product: "",
      searchValue: "",
      page: 1,
      pageSize: pageSize,
    };
    
    fetchTrafficLightData(payload);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchTrafficLightData();
    setIsRefreshing(false);
  };

  const handleViewDetails = (contract) => {
    setSelectedContract(contract);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedContract(null);
    if (viewProduct){
      navigate("/data-traffic-lights", { replace: true });
    }
  };

  const handlePageChange = async (newPage) => {
    setCurrentPage(newPage);

    const payload = {
      parentDomain: appliedParentDomain === "All Parent Domains" ? "" : appliedParentDomain,
      domain: appliedDomain === "All Domains" ? "" : appliedDomain,
      subDomain: appliedSubDomain === "All Sub Domains" ? "" : appliedSubDomain,
      product: appliedProduct === "All Products" ? "" : appliedProduct,
      searchValue: "",
      page: newPage,
      pageSize: pageSize,
    };

    await fetchTrafficLightData(payload);
  };

  if (loading) {
    return (
        // <div className="relative bg-white flex items-center justify-center z-50 min-h-screen">
        //      <div className="flex flex-col items-center space-y-3">
        //        <img src={heartLoading} alt="Loading..." width={200} />
        //        <span className="text-gray-700 text-[20px] text-center">
        //      Hang tight! The resources are loading...
        //        </span>
        //      </div>
        //    </div>
           <div className="relative bg-white flex items-center justify-center z-50 h-[80vh]">
                     <div className="flex flex-col items-center space-y-3">
                       <img src={heartLoading} alt="Loading..." width={200} />
                       <span className="text-gray-700 text-[20px] text-center">
 Hang tight! The resources are loading...
                       </span>
                     </div>
                   </div>
    );
  }

  return (
    <div className='min-h-screen bg-[#f9fafb] px-6 py-2'>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center items-center lg:justify-between pb-3">
        <div>
          <h1 className="font-bold pt-4 font-family-poppins text-[24px] text-[#424242]">Data Traffic Light</h1>
          <p className="pb-2">Real-time monitoring of data contract health and compliance</p>
        </div>
        
        <div className="flex items-center gap-4 mt-1">
          <div className="flex items-center gap-4 bg-white rounded-lg p-3 shadow-sm border">
            <div className="flex items-center gap-2">
              <Tooltip title="All underlying assets have passed their data quality rules and in turn the dimensions, in the latest pipeline run." placement='top' arrow ><InfoIcon sx={{color: "green"}} /></Tooltip>
              <span className="text-sm font-medium text-gray-700">{stats.healthy} Healthy</span>
            </div>
            <div className="flex items-center gap-2">
            <Tooltip title="One or more rules of data asset(s) within the product have failed, but dimensions(s) of each asset(s) have passed.  " placement='top' arrow ><InfoIcon sx={{color: "#FFBF00"}} /></Tooltip>

              <span className="text-sm font-medium text-gray-700">{stats.warning} Warning</span>
            </div>
            <div className="flex items-center gap-2">
            <Tooltip title=" One or more rules of data assets within the product have failed, and dimension(s) of asset(s) have also failed." placement='top' arrow ><InfoIcon sx={{color: "red"}} /></Tooltip>

              <span className="text-sm font-medium text-gray-700">{stats.critical} Critical</span>
            </div>
          </div>
        </div>
      </div>

     

      {/* Filters Section */}
      <div className="bg-white p-2 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[45px] whitespace-nowrap ${
                showFilters
                  ? 'bg-[#7dc1a9] text-white border-[#7dc1a9]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            <button
              onClick={handleResetFilters}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-g rounded-lg hover:bg-[#bb1ccc] flex items-center gap-2 h-[45px] whitespace-nowrap"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          </div>
          
          <div className="flex-1"></div>
        </div>

        {/* Filters - Collapsible */}
 {showFilters && (
  <div className="flex flex-col lg:flex-row items-start lg:items-end gap-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
    {/* Parent Domain */}
    <div className="flex-1 min-w-[200px]">
      <label className="block text-[14px] font-medium text-gray-700 mb-1">
        Parent Domain
      </label>
      <Select
        value={
          parentDomain && parentDomain !== "All Parent Domains"
            ? { value: parentDomain, label: parentDomain }
            : null  // null = no X button shown
        }
        onChange={(selected) => {
          handleParentDomainChange({
            target: { value: selected ? selected.value : "All Parent Domains" }
          });
        }}
        options={
          getFilterOptions().parentDomains.map((option) => ({
            value: option,
            label: option
          }))
        }
        placeholder="All Parent Domains"
        isClearable  // X appears ONLY when value is selected
        isSearchable
        styles={{
          control: (provided, state) => ({
            ...provided,
            minHeight: '45px',
            height: '45px',
            borderColor: state.isFocused ? '#bb1ccc' : '#d1d5db',
            borderRadius: '0.5rem',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 0, 160, 0.2)' : 'none',
            '&:hover': {
              borderColor: state.isFocused ? '#bb1ccc' : '#d1d5db',
            },
          }),
          valueContainer: (provided) => ({
            ...provided,
            height: '45px',
            padding: '0 12px',
          }),
          input: (provided) => ({
            ...provided,
            margin: '0',
            padding: '0',
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            height: '45px',
          }),
          placeholder: (provided) => ({
            ...provided,
            color: '#9ca3af',
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
              ? 'rgba(0, 0, 160, 0.1)'
              : state.isFocused
              ? 'rgba(0, 0, 160, 0.05)'
              : 'white',
            color: state.isSelected ? '#bb1ccc' : '#374151',
            '&:active': {
              backgroundColor: 'rgba(0, 0, 160, 0.15)',
            },
          }),
        }}
      />
    </div>

    {/* Domain */}
    <div className="flex-1 min-w-[200px]">
      <label className="block text-[14px] font-medium text-gray-700 mb-1">
        Domain
      </label>
      <Select
        value={
          domain && domain !== "All Domains"
            ? { value: domain, label: domain }
            : null
        }
        onChange={(selected) => {
          handleDomainChange({
            target: { value: selected ? selected.value : "All Domains" }
          });
        }}
        options={
          getFilterOptions().domains.map((option) => ({
            value: option,
            label: option
          }))
        }
        placeholder="All Domains"
        isClearable
        isSearchable
        styles={{
          control: (provided, state) => ({
            ...provided,
            minHeight: '45px',
            height: '45px',
            borderColor: state.isFocused ? '#bb1ccc' : '#d1d5db',
            borderRadius: '0.5rem',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 0, 160, 0.2)' : 'none',
            '&:hover': {
              borderColor: state.isFocused ? '#bb1ccc' : '#d1d5db',
            },
          }),
          valueContainer: (provided) => ({
            ...provided,
            height: '45px',
            padding: '0 12px',
          }),
          input: (provided) => ({
            ...provided,
            margin: '0',
            padding: '0',
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            height: '45px',
          }),
          placeholder: (provided) => ({
            ...provided,
            color: '#9ca3af',
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
              ? 'rgba(0, 0, 160, 0.1)'
              : state.isFocused
              ? 'rgba(0, 0, 160, 0.05)'
              : 'white',
            color: state.isSelected ? '#bb1ccc' : '#374151',
            '&:active': {
              backgroundColor: 'rgba(0, 0, 160, 0.15)',
            },
          }),
        }}
      />
    </div>

    {/* Sub Domain */}
    <div className="flex-1 min-w-[200px]">
      <label className="block text-[14px] font-medium text-gray-700 mb-1">
        Sub Domain
      </label>
      <Select
        value={
          subDomain && subDomain !== "All Sub Domains"
            ? { value: subDomain, label: subDomain }
            : null
        }
        onChange={(selected) => {
          handleSubDomainChange({
            target: { value: selected ? selected.value : "All Sub Domains" }
          });
        }}
        options={
          getFilterOptions().subDomains.map((option) => ({
            value: option,
            label: option
          }))
        }
        placeholder="All Sub Domains"
        isClearable
        isSearchable
        styles={{
          control: (provided, state) => ({
            ...provided,
            minHeight: '45px',
            height: '45px',
            borderColor: state.isFocused ? '#bb1ccc' : '#d1d5db',
            borderRadius: '0.5rem',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 0, 160, 0.2)' : 'none',
            '&:hover': {
              borderColor: state.isFocused ? '#bb1ccc' : '#d1d5db',
            },
          }),
          valueContainer: (provided) => ({
            ...provided,
            height: '45px',
            padding: '0 12px',
          }),
          input: (provided) => ({
            ...provided,
            margin: '0',
            padding: '0',
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            height: '45px',
          }),
          placeholder: (provided) => ({
            ...provided,
            color: '#9ca3af',
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
              ? 'rgba(0, 0, 160, 0.1)'
              : state.isFocused
              ? 'rgba(0, 0, 160, 0.05)'
              : 'white',
            color: state.isSelected ? '#bb1ccc' : '#374151',
            '&:active': {
              backgroundColor: 'rgba(0, 0, 160, 0.15)',
            },
          }),
        }}
      />
    </div>

    {/* Product */}
    <div className="flex-1 min-w-[200px]">
      <label className="block text-[14px] font-medium text-gray-700 mb-1">
        Product
      </label>
      <Select
        value={
          product && product !== "All Products"
            ? { value: product, label: product }
            : null
        }
        onChange={(selected) => {
          handleProductChange({
            target: { value: selected ? selected.value : "All Products" }
          });
        }}
        options={
          getFilterOptions().products.map((option) => ({
            value: option,
            label: option
          }))
        }
        placeholder="All Products"
        isClearable
        isSearchable
        styles={{
          control: (provided, state) => ({
            ...provided,
            minHeight: '45px',
            height: '45px',
            borderColor: state.isFocused ? '#bb1ccc' : '#d1d5db',
            borderRadius: '0.5rem',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 0, 160, 0.2)' : 'none',
            '&:hover': {
              borderColor: state.isFocused ? '#bb1ccc' : '#d1d5db',
            },
          }),
          valueContainer: (provided) => ({
            ...provided,
            height: '45px',
            padding: '0 12px',
          }),
          input: (provided) => ({
            ...provided,
            margin: '0',
            padding: '0',
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            height: '45px',
          }),
          placeholder: (provided) => ({
            ...provided,
            color: '#9ca3af',
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
              ? 'rgba(0, 0, 160, 0.1)'
              : state.isFocused
              ? 'rgba(0, 0, 160, 0.05)'
              : 'white',
            color: state.isSelected ? '#bb1ccc' : '#374151',
            '&:active': {
              backgroundColor: 'rgba(0, 0, 160, 0.15)',
            },
          }),
        }}
      />
    </div>

    {/* Apply Filters Button */}
    <div className="flex items-end">
      <button
        onClick={handleApplyFilters}
       disabled={parentDomain === "All Parent Domains" && domain === "All Domains" && subDomain === "All Sub Domains" && product === "All Products"}
        className="px-6 py-2.5 text-sm font-medium text-white bg-[#7dc1a9] rounded-lg hover:bg-[#7dc1a9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-[45px] whitespace-nowrap"
      >
        Apply Filters
      </button>
    </div>
  </div>
)}
      </div>

     
      {filterLoading ? (
                  <div className="relative bg-white flex items-center justify-center z-50 h-[60vh]">
                     <div className="flex flex-col items-center space-y-3">
                       <img src={heartLoading} alt="Loading..." width={200} />
                       <span className="text-gray-700 text-[20px] text-center">
Loading contracts...                       </span>
                     </div>
                   </div>
      ) : (
        <>
          {/* Contract Cards */}
          <div className='px-4' >
          <div className="grid [grid-template-columns:repeat(auto-fit,minmax(296px,296px))] gap-4 justify-start">
            {filteredContracts.map((contract) => (
              <DataContractCard 
                key={contract.id} 
                {...contract} 
                onViewDetails={() => handleViewDetails(contract)}
              />
            ))}
          </div>
          </div>

          {/* No Results State */}
          {filteredContracts.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-gray-400">📊</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No contracts found</h3>
              <p className="text-gray-600">
                {parentDomain !== 'All Parent Domains' || domain !== 'All Domains' || subDomain !== 'All Sub Domains' || product !== 'All Products'
                  ? 'Try adjusting your filter criteria'
                  : 'No data contracts are currently configured'
                }
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 px-4">
              <span className="text-sm text-gray-600">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <div className="flex items-center space-x-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={i}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 text-sm font-medium rounded-md ${
                          currentPage === pageNum
                            ? 'bg-[#0171C1] text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Contract Details Modal */}
      <DataContractDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        contract={selectedContract}
      />
    </div>
  );
}

export default LandingPage;
